/**
 * 파일명 : pjt0103m.tsx
 * 설명 : 프로젝트 팀원 등록 화면
 * 수정내역
 ************************************************
 * Version	   수정일자			수정자	    수정내용      
 ************************************************
 * 1.0		2023-03-14			    최초작성
 **/

/************************************************
 * Import 영역  
 ************************************************/
/* React */
import React from 'react';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Pjt0103mAction } from '../../../redux/slice/pjt/pjt0103m_slice';
import { IInquiryCndsState, IPjtCdItemState, IPjtMemberItemState, IProjectItemState, IComCdItemState, IUserItemState } from '../../../redux/models/pjt/pjt0103m_model';


/* AntD */
import { Layout, Row, Col, Space, Descriptions, Button, Typography, Select, Collapse, Input, Table } from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

/* 업무 */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';

const Pjt0103m = (props:any) => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch = useAppDispatch();
    const pjtCdList   = useAppSelector((state) => state.Pjt0103m.pjtCdList);
    const userList    = useAppSelector((state) => state.Pjt0103m.userList);
    const cmbList029  = useAppSelector((state) => state.Pjt0103m.cmbList029);
    const pjtItems    = useAppSelector((state) => state.Pjt0103m.pjtItems);
    const pjtMmbrList = useAppSelector((state) => state.Pjt0103m.pjtMmbrList);
    const inquiryCnds = useAppSelector((state) => state.Pjt0103m.inquiryCnds);
    const current     = useAppSelector((state) => state.Pjt0103m.current);
    const pageSize    = useAppSelector((state) => state.Pjt0103m.pageSize);

    /* AntD */
    const { Content } = Layout;
    const { Text } = Typography;
    const { Panel } = Collapse;
    const { Option } = Select;


    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>번호</div>,
                dataIndex: 'no',
                key: 'no',
                align: 'center',
                width: '5%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>팀원</div>,
                dataIndex: 'memUserNm',
                key: 'memUserNm',
                align: 'center',
                width: '10%',
                render: (_:string, record:IPjtMemberItemState) => <Button className='link-button' size='small' onClick={() => showDetail(record)}>{record.memUserNm}</Button>
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>팀원ID</div>,
                dataIndex: 'memUserId',
                key: 'memUserId',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>소속</div>,
                dataIndex: 'belongDcd',
                key: 'belongDcd',
                align: 'center',
                width: '10%',
                render: (text:string, _:IPjtMemberItemState) => {
                    const cmbItem = cmbList029.find(item => item.value === text);
                    return cmbItem ? cmbItem.label : '해당없음';
                }
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>투입일자</div>,
                dataIndex: 'realInDt',
                key: 'realInDt',
                align: 'center',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
                width: '8%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>철수일자</div>,
                dataIndex: 'realOutDt',
                key: 'realOutDt',
                align: 'center',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
                width: '8%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>상세보기</div>,
                key: 'action',
                render: (_:string, record:IPjtMemberItemState) => (
                    <Button
                        id='btn_showDetail'
                        name='btn_showDetail'
                        className='background-color-default-button'
                        size='small'
                        onClick={() => {
                            showDetail(record);
    
                        }}
    
                    >
                        상세보기
                    </Button>
                ),
                align: 'center',
                width: '5%',
            }];
            return arr;
    }, [pjtMmbrList]);

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     **/
    React.useEffect(() => {
        // 사용자 조회
        if (!userList || userList.length < 1) fn_User();

        // 전체프로젝트 조회
        if (!pjtCdList || pjtCdList.length < 1) fnProjectList();

        // 팀원구분 조회
        if (!cmbList029 || cmbList029.length < 1) fnCmb029();
    }, []);

    /**
     * 기능 : fn_User
     * 설명 : 직원명 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_User = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: 'A',
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1)
                dispatch(Pjt0103mAction.setUserList(responseData.userList));
            },
        });
    };

    const userOptions = React.useCallback(() => {
        let arrUserOptions: IUserItemState[] = [];

        if (common.isAdmin() || common.isDeptHdr()) {
            arrUserOptions.push({label: '전체', value: ''});
        }

        userList.forEach((item:IUserItemState) => {
            if (!common.isAdmin() && !common.isDeptHdr()) {
                if (item.empNo === common.getEmpNo()) {
                    arrUserOptions.push({
                        label: item.userNm,
                        value: item.empNo
                    });
                }
            } else {
                arrUserOptions.push({
                    label: item.userNm,
                    value: item.empNo
                });
            }
        });

        return arrUserOptions;
    }, [userList]);

    /**
     * 기능 : 상세보기(그리드선택시)
     * param : 
     */
    const showDetail = (record:IPjtMemberItemState) => {
        const callback = () => {
            fnSearchProject(null);

        };

        // 기존 등록된 팀원 수정 및 삭제 - 거래구분코드 ('02')
        // 기존 등록된 팀원 등록 - 거래구분코드에 따른 삭제여부 'N' => 기등록된 팀원의 삭제여부는 'N'
        const params:any = {};
        params.title = '팀원상세보기';
        params.popupId = 'pjt0103p';
        params.accept = callback;
        params.width = 1350
        params.props = {
            pjCd: pjtItems.pjCd,
            cusNm: pjtItems.cusNm,
            pmEmpNo: pjtItems.pmEmpNo,
            pjtMember : record,
            trDsc: '02',
            trDelCd: 'Y'
        };
        common.openPopup(params);

    }

    const fnProjectList = () => {
        const arrUserOptions:IPjtCdItemState[] = [];

        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            //endYn: 'N'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRPJTR0002',
            success: (responseData:any) => {
                responseData.projectList.sort((a:IProjectItemState, b:IProjectItemState) => (a.pjNm > b.pjNm) ? 1 : -1);
                responseData.projectList.forEach((item:IProjectItemState) => {
                    arrUserOptions.push({
                        label: item.pjNm,
                        value: item.pjCd
                    });
                });
                
                dispatch(Pjt0103mAction.setPjtCdList(arrUserOptions));
            },
        });
    }

    React.useEffect(() => {
        if (pjtCdList && pjtCdList.length > 0 && props && !common.isEmptyString(props.pjCd)) {
            dispatch(Pjt0103mAction.setInquiryCnds({ ...inquiryCnds, pjCd : props.pjCd }));
            fnSearchProject(props.pjCd);
        }
    }, [pjtCdList]);

    /**
     * 기능 : fnMemList
     * 설명 : 프로젝트팀원내역 조회
     * param : 
     * return : None
     **/
    const fnMemList = (pjCd:string) => {
        dispatch(Pjt0103mAction.setCurrent(1));
        dispatch(Pjt0103mAction.setPjtMmbrList([]));
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData:IInquiryCndsState = {
            ...inquiryCnds,
            pjCd : !common.isEmptyString(pjCd) ? pjCd : inquiryCnds.pjCd
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRPJTR0004',
            success: (responseData:any) => {
                dispatch(Pjt0103mAction.setPjtMmbrList(responseData.pjtMemList));
            },
        });
    };
    /**
     * 기능 : 공통코드 조회 - 소속코드
     * param : 
     */
     const fnCmb029 = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IComCdItemState[]) => {
                let arrOptions:IComCdItemState[] = [];
                arrOptions.push({label: '전체', value: ''});

                datas.forEach((item:IComCdItemState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd
                    });
                });
                dispatch(Pjt0103mAction.setCmbList029(arrOptions));
            }

        };
        common.getComCodeList('029', option);
    };

    /************************************************
     * 이벤트 함수 영역
     ************************************************/
    /**
     * 기능 : 초기화 함수
     * param : 
     **/
    const fn_Reset = () => {
        dispatch(Pjt0103mAction.initState());
    };

    /**
     * 기능 : 검색조건으로 프로젝트 조회
     * param :
     */
    const fnSearchProject = (pjCd:string) => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData:IInquiryCndsState = {
            ...inquiryCnds,
            pjCd : !common.isEmptyString(pjCd) ? pjCd : inquiryCnds.pjCd
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRPJTR0003',
            success: (responseData:any) => {
                dispatch(Pjt0103mAction.setPjtItems(responseData.pjtInfo));
                fnMemList(!common.isEmptyString(pjCd) ? pjCd : inquiryCnds.pjCd);
            },
        });
    }

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btnreset': {
                fn_Reset();
                break;
            }

            case 'btnList': {
                const param = {
                    menuId : 'MPJT2023002',
                };
                common.openPage(param);
                break;
            }

            case 'btn_newcrew': {
                if (!pjtItems || common.isEmptyString(pjtItems.pjCd)) {
                    const param = {
                        title : '위드정보 확인',
                        contents: '먼저 프로젝트를 조회하세요.',
                    };
                    common.openInfo(param);
                    return;
                }
                const callback = () => {
                    fnSearchProject(null);

                };
                // 새로운 팀원 등록 - 거래구분코드 ('01')
                // 새로운 팀원 등록 - 거래구분코드에 따른 삭제여부 'Y' => 삭제된 팀원이 있는지 확인한 후 있으면 update, 없으면 insert
                const params:any = {};
                params.title = '새로운 팀원등록';
                params.popupId = 'pjt0103p';
                params.accept = callback;
                params.width = 1350
                params.props = {
                    pjCd: pjtItems.pjCd,
                    cusNm: pjtItems.cusNm,
                    pmEmpNo: pjtItems.pmEmpNo,
                    pjtMemList : pjtMmbrList,
                    trDsc: '01',
                    trDelCd: 'Y'
                };
                common.openPopup(params);
                break;
            }

            case 'btnProSearch': {
                // 프로젝트명 입력하지 않았을 경우 조회되지 않도록
                if (common.isEmptyString(inquiryCnds.pjCd)) {
                    const param = {
                        title : '위드정보 확인',
                        contents: '프로젝트를 선택하세요.',
                    };
                    common.openInfo(param);
                    return;
                }

                // 프로젝트 내역 조회
                fnSearchProject(null);
                break;
            }

            default:
        }
    };

    /**
     * 기능 : 입력 후 Enter key 입력 시 조회
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */
    const handleOnKeyUp = (e:any) => {
        if (e.key === 'Enter') {
            fnSearchProject(null); // 조회
        }
    };

    return (
        /* 화면 디자인 영역 */
        <Layout>
            <Content>
                <Collapse
                    bordered={false}
                    defaultActiveKey={['1']}
                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    className='site-collapse-custom-collapse'
                >
                    <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                        <Row>
                            <Col flex='9.5'>
                                <Space direction='horizontal' size='large'>
                                    <Space size='small'>
                                        <label>프로젝트명</label>
                                        <Select 
                                            showSearch
                                            placeholder='프로젝트를 선택하세요.'
                                            style={{
                                                width: '100%',
                                                minWidth: 500,
                                            }}
                                            optionFilterProp='label'
                                            filterSort={(optionA: any, optionB: any) => {
                                                const labelA = (optionA?.label ?? '').toLowerCase();
                                                const labelB = (optionB?.label ?? '').toLowerCase();
                                                return labelA.localeCompare(labelB);
                                            }}
                                            options={pjtCdList}
                                            onKeyUp={handleOnKeyUp}
                                            value={inquiryCnds.pjCd}
                                            onChange={val => dispatch(Pjt0103mAction.setInquiryCnds({...inquiryCnds, pjCd: val}))}
                                        ></Select>
                                    </Space>
                                    <Space size='small'>
                                        <label>직원구분</label>
                                        <Select 
                                            style={{
                                                width: '100%',
                                                minWidth: 200,
                                            }}
                                            optionFilterProp='label'
                                            defaultValue=''
                                            filterSort={(optionA: any, optionB: any) => {
                                                const labelA = (optionA?.label ?? '').toLowerCase();
                                                const labelB = (optionB?.label ?? '').toLowerCase();
                                                if (labelA === '전체') return -1;
                                                if (labelB === '전체') return 1;
                                                return labelA.localeCompare(labelB);
                                            }}
                                            options={cmbList029}
                                            onKeyUp={handleOnKeyUp}
                                            value={inquiryCnds.belongDcd}
                                            onChange={val => dispatch(Pjt0103mAction.setInquiryCnds({...inquiryCnds, belongDcd: val}))}
                                        ></Select>
                                    </Space>
                                    <Space size='small'>
                                        <label>직원명</label>
                                        <Select 
                                            showSearch
                                            style={{
                                                width: 120,
                                            }}
                                            defaultValue=''
                                            optionFilterProp='label'
                                            filterSort={(optionA: any, optionB: any) => {
                                                const labelA = (optionA?.label ?? '').toLowerCase();
                                                const labelB = (optionB?.label ?? '').toLowerCase();
                                                
                                                if (labelA === '전체') return -1;
                                                if (labelB === '전체') return 1;
                                                
                                                return labelA.localeCompare(labelB);
                                            }}
                                            options={userOptions()}
                                            onKeyUp={handleOnKeyUp}
                                            value={inquiryCnds.memEmpNo}
                                            onChange={val => dispatch(Pjt0103mAction.setInquiryCnds({...inquiryCnds, memEmpNo: val}))}
                                            // ReadOnly 적용
                                            {...common.getReadonlySelectboxProps(!common.isAdmin() && !common.isDeptHdr())}
                                        ></Select>
                                    </Space>
                                </Space>
                            </Col>
                            <Col flex='0.5'>
                                <Space>
                                    <Button
                                        name='btnreset'
                                        className='background-color-default-button'
                                        onClick={onClick}
                                    >
                                        초기화
                                    </Button>
                                    <Button
                                        name='btnList'
                                        className='background-color-default-button'
                                        onClick={onClick}
                                    >
                                        목록보기
                                    </Button>

                                    <Button
                                        name='btn_newcrew'
                                        className='background-color-primary-button'
                                        onClick={onClick}
                                        disabled={!common.isAdmin() && common.getEmpNo() !== pjtItems.pmEmpNo}
                                        >
                                    
                                        + 신규팀원등록
                                    </Button>
                                    <Button
                                        name='btnProSearch'
                                        className='background-color-primary-button'
                                        icon={<SearchOutlined />}
                                        onClick={onClick}
                                    >
                                        조회
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Panel>
                </Collapse>
                <Row>
                    <Col style={{ width: '100%', marginBottom: 20 }}>
                        <img className='title-icon' alt='' />
                        <Text strong>프로젝트정보</Text>

                        <Descriptions bordered column={4} size='small'>

                            <Descriptions.Item label='프로젝트 기간'>
                                {pjtItems.pmEmpNo ? commonDate.maskDate(pjtItems.pjStartDt, 'YYYY-MM-DD') + ' ~ ' + commonDate.maskDate(pjtItems.pjEndDt, 'YYYY-MM-DD')
                                : <Input bordered={false} style={{width:5}}></Input>}
                            </Descriptions.Item>

                            <Descriptions.Item label='Project Manager'>
                                {pjtItems.pmUserNm}
                            </Descriptions.Item>
                            <Descriptions.Item label='업체명'>
                                {pjtItems.cusNm}
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>
                </Row>
                <Table
                    dataSource={pjtMmbrList}
                    key='no'
                    columns={columns()}
                    size='small'
                    style={{width:'100%'}}
                    scroll={{y:550}}
                    pagination={{
                        pageSize: pageSize,
                        current: current,
                        showTotal : () => '총 ' + pjtMmbrList.length + ' 건',
                        onChange: (page, pageSize) => {
                            dispatch(Pjt0103mAction.setCurrent(page));
                            dispatch(Pjt0103mAction.setPageSize(pageSize));
                        },
                    }}
                />
            </Content>
        </Layout>
    );
}

export default Pjt0103m;
